import { useState } from 'react'
import { Scheduled } from '../store/jobs'
import { updateScheduled, deleteScheduled } from '../api/client'
import LoadingButton from './LoadingButton'
import CopyButton from './CopyButton'
import ErrorMessage from './ErrorMessage'

interface CalendarModalProps {
  item: Scheduled
  onClose: () => void
  onUpdate: (updated: Partial<Scheduled>) => void
  onDelete: () => void
  volunteerBaseUrl: string
}

export default function CalendarModal({
  item,
  onClose,
  onUpdate,
  onDelete,
  volunteerBaseUrl,
}: CalendarModalProps) {
  const [datetime, setDatetime] = useState(
    item.scheduled_datetime.slice(0, 16)
  )
  const [instructions, setInstructions] = useState(item.instruction_text || '')
  const [proofEmail, setProofEmail] = useState(item.on_proof_email || '')
  const [proofWebhook, setProofWebhook] = useState(item.on_proof_webhook || '')
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  const volunteerLink = `${volunteerBaseUrl}/volunteer/${item.scheduled_id}`

  const canEdit = !item.fired

  async function handleSave() {
    if (!canEdit) return
    setLoading(true)
    setError(null)
    try {
      await updateScheduled(item.scheduled_id, {
        scheduled_datetime: new Date(datetime).toISOString(),
        instruction_text: instructions || undefined,
        on_proof_email: proofEmail || undefined,
        on_proof_webhook: proofWebhook || undefined,
      })
      onUpdate({
        scheduled_datetime: new Date(datetime).toISOString(),
        instruction_text: instructions,
        on_proof_email: proofEmail || undefined,
        on_proof_webhook: proofWebhook || undefined,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update')
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete() {
    if (!canEdit) return
    setDeleteLoading(true)
    setError(null)
    try {
      await deleteScheduled(item.scheduled_id)
      onDelete()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setDeleteLoading(false)
    }
  }

  function getStatusBadge() {
    if (item.proof_url) return { text: 'Posted', class: 'bg-green-100 text-green-800' }
    if (item.fired) return { text: 'Sent', class: 'bg-yellow-100 text-yellow-800' }
    return { text: 'Scheduled', class: 'bg-blue-100 text-blue-800' }
  }

  const status = getStatusBadge()

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-lg font-semibold">Scheduled Post</h2>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.class}`}>
            {status.text}
          </span>
        </div>

        {error && <ErrorMessage message={error} className="mb-4" onDismiss={() => setError(null)} />}

        {/* Preview */}
        <div className="flex gap-4 mb-4">
          <div className="w-24 h-40 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
            {item.thumbnail_url || item.media_url ? (
              item.media_type === 'video' ? (
                <video
                  src={item.media_url}
                  poster={item.thumbnail_url}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img
                  src={item.thumbnail_url || item.media_url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                {item.media_type}
              </div>
            )}
          </div>
          <div className="flex-1 text-sm">
            <p className="text-gray-600 line-clamp-4">{item.caption}</p>
            {item.hashtags && item.hashtags.length > 0 && (
              <p className="text-xs text-gray-400 mt-1 truncate">
                {item.hashtags.join(' ')}
              </p>
            )}
          </div>
        </div>

        {/* Volunteer Link */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Volunteer Link</span>
            <CopyButton text={volunteerLink} label="Copy" />
          </div>
          <p className="text-xs text-gray-600 truncate mt-1 font-mono">{volunteerLink}</p>
        </div>

        {/* Edit Form */}
        {canEdit ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedule Date & Time
              </label>
              <input
                type="datetime-local"
                value={datetime}
                onChange={(e) => setDatetime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <details>
              <summary className="cursor-pointer text-sm font-medium text-gray-700">
                Advanced Options
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows={3}
                    placeholder="Special instructions for posting..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proof Notify Email
                  </label>
                  <input
                    type="email"
                    value={proofEmail}
                    onChange={(e) => setProofEmail(e.target.value)}
                    placeholder="proof-notify@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Get notified when proof is submitted.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proof Webhook
                  </label>
                  <input
                    type="url"
                    value={proofWebhook}
                    onChange={(e) => setProofWebhook(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </details>
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic">
            This post has already been sent and cannot be edited.
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          {canEdit && !showConfirmDelete && (
            <LoadingButton
              type="button"
              variant="danger"
              onClick={() => setShowConfirmDelete(true)}
              className="flex-shrink-0"
            >
              Delete
            </LoadingButton>
          )}
          {showConfirmDelete && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600">Delete this post?</span>
              <LoadingButton
                type="button"
                variant="danger"
                size="sm"
                loading={deleteLoading}
                onClick={handleDelete}
              >
                Yes
              </LoadingButton>
              <button
                type="button"
                onClick={() => setShowConfirmDelete(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                No
              </button>
            </div>
          )}
          <div className="flex-1" />
          <LoadingButton
            type="button"
            onClick={onClose}
            variant="secondary"
          >
            {canEdit ? 'Back' : 'Close'}
          </LoadingButton>
          {canEdit && (
            <LoadingButton
              type="button"
              onClick={handleSave}
              loading={loading}
              variant="primary"
            >
              Save
            </LoadingButton>
          )}
        </div>
      </div>
    </div>
  )
}
